import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, User, Clock, ArrowRight } from "lucide-react";
import { useFormNotifications } from "@/hooks/useFormNotifications";

interface NameEmailInlineFormProps {
  phoneNumber: string;
  onComplete: () => void;
  showCountdown?: boolean;
  countdown?: number;
}

const NameEmailInlineForm = ({ phoneNumber, onComplete, showCountdown = false, countdown = 600 }: NameEmailInlineFormProps) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { notifyNameEmailSubmission } = useFormNotifications();

  const formatCountdown = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Fire GTM event for name/email submission
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({ event: 'cta_name_email_submit' });
    }
    
    // Send to Telegram if name and email are provided
    if (name && email) {
      await notifyNameEmailSubmission(name, email, 'hero_contact');
    }
    
    // Add system message to chat session
    const sessionId = localStorage.getItem('visitor_session_id');
    if (sessionId && (name || email)) {
      import('@/utils/chatSystemMessages').then(({ addSystemMessage }) => {
        addSystemMessage(sessionId, `📧 Contact info submitted: ${name || 'Anonymous'} (${email || 'No email'})`);
      });
    }
    
    setTimeout(() => {
      onComplete();
    }, 1000);
  };

  const handleSkip = () => {
    // Fire GTM event for form skip
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({ event: 'cta_name_email_skip' });
    }
    
    onComplete();
  };

  return (
    <div className="space-y-4">
      {showCountdown && countdown && (
        <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 border border-green-500/30 rounded-lg p-4 backdrop-blur-sm mb-4">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-green-400 text-sm font-medium">Callback in progress</span>
          </div>
          <div className="flex items-center justify-center gap-2">
            <Clock className="w-5 h-5 text-[#D9992B]" />
            <span className="text-2xl font-bold text-[#D9992B] font-mono">
              {formatCountdown(countdown)}
            </span>
          </div>
          <p className="text-xs text-green-300 mt-2">Expected callback time</p>
        </div>
      )}
      
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold text-[#F4F4F4] mb-2">
          Secure Your Priority Service
        </h3>
        <p className="text-sm text-[#CCCCCC]">
          Get updates and ensure we can reach you through multiple channels
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="relative">
          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#747DA1]" />
          <Input
            type="text"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="pl-10 border-[#747DA1] focus:border-[#D9992B] focus:ring-[#D9992B] bg-[#2B2F4A] text-[#F4F4F4] placeholder:text-[#747DA1]"
          />
        </div>
        
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#747DA1]" />
          <Input
            type="email"
            placeholder="Your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="pl-10 border-[#747DA1] focus:border-[#D9992B] focus:ring-[#D9992B] bg-[#2B2F4A] text-[#F4F4F4] placeholder:text-[#747DA1]"
          />
        </div>
        
        <div className="flex gap-2">
          <Button 
            type="submit"
            disabled={isSubmitting}
            className="flex-1 bg-[#D9992B] hover:bg-[#c8881a] text-white py-3 font-semibold"
          >
            {isSubmitting ? "Saving..." : "Save & Continue"}
          </Button>
          
          <Button 
            type="button"
            onClick={handleSkip}
            className="px-6 bg-[#747DA1] hover:bg-[#5f6589] text-white py-3 font-semibold"
          >
            Skip
          </Button>
        </div>
      </form>
    </div>
  );
};

export default NameEmailInlineForm;